import { createContext, type ReactNode, useCallback, useContext } from 'react';

import { type CapturedChart, ShareCaptureScope } from '@revelio/replots';

import type { PositionedItem } from '../PageConfigProvider/types/item';
import {
  type ReplotsChartName,
  useSharedReportData,
} from '../SharedReport/SharedReportDataContext';

interface ChartContextValue {
  item: PositionedItem;
  variant: 'default' | 'report';
}

const ChartContext = createContext<ChartContextValue | undefined>(undefined);

interface ChartProviderProps {
  item: PositionedItem;
  variant?: 'default' | 'report';
  children: ReactNode;
}

export const ChartProvider = ({
  item,
  variant,
  children,
}: ChartProviderProps) => {
  const sharedReportData = useSharedReportData();
  const register = useCallback(
    (scopeKey: string, capture: CapturedChart) => {
      sharedReportData?.updateChartData(scopeKey, {
        component: capture.component as ReplotsChartName,
        props: capture.props,
      });
    },
    [sharedReportData]
  );

  const content = (
    <ChartContext.Provider value={{ item, variant: variant ?? 'default' }}>
      {children}
    </ChartContext.Provider>
  );

  // Skip the scope when no share session is active so `withShareCapture`
  // becomes a no-op and avoids the per-render prop stringify.
  if (!sharedReportData) return content;

  return (
    <ShareCaptureScope scopeKey={item.id} register={register}>
      {content}
    </ShareCaptureScope>
  );
};

export const useChart = () => {
  const context = useContext(ChartContext);
  if (!context) {
    throw new Error('useChart must be used within a ChartProvider');
  }
  return context;
};

export const useSafeChart = () => {
  const context = useContext(ChartContext);
  if (!context) {
    return {
      item: undefined,
      variant: 'default' as const,
    };
  }
  return context;
};
